import { useUpdateCandidate } from "@vidario/api-client";
import {
  computeSkillMatch,
  criteriaToSkillSplit,
  type Candidate,
  type Job,
} from "@vidario/domain";
import { Card, SkillTag, useToast } from "@vidario/ui";
import { Check, X, Zap } from "lucide-react";
import { type ReactElement } from "react";

export interface SkillMatchCardProps {
  /** URL-derived jobId (string), used for cache invalidation. */
  jobId: string;
  job: Job;
  candidate: Candidate;
  /** Allow click-to-toggle overrides. Recruiter + admin can toggle
   * (MVP §4.2: "Jedes Häkchen ist manuell überschreibbar durch
   * Recruiter oder Admin"). */
  canEdit: boolean;
}

/** Skill-Abgleich section. Shows Must-Haves, Should-Haves and the
 * remaining flat candidate skills. Each Must/Should entry is a toggle
 * that persists to `candidates.skillMatch` on click. */
export function SkillMatchCard({
  jobId,
  job,
  candidate,
  canEdit,
}: SkillMatchCardProps): ReactElement {
  const { mustHaves: jobMustHaves, shouldHaves: jobShouldHaves } =
    criteriaToSkillSplit(job.criteria);
  const match = computeSkillMatch(candidate, jobMustHaves, jobShouldHaves);
  const otherSkills = candidate.skills.filter((s) => {
    const n = s.toLowerCase();
    const isMust = jobMustHaves.some((m) => m.toLowerCase() === n);
    const isShould = jobShouldHaves.some((m) => m.toLowerCase() === n);
    return !isMust && !isShould;
  });
  const updateCandidate = useUpdateCandidate();
  const toast = useToast();

  const toggle = (bucket: "mustHaves" | "shouldHaves", skill: string, currentMet: boolean): void => {
    if (!canEdit) return;
    const next = {
      mustHaves: { ...candidate.skillMatch.mustHaves },
      shouldHaves: { ...candidate.skillMatch.shouldHaves },
    };
    next[bucket] = { ...next[bucket], [skill]: !currentMet };
    updateCandidate.mutate(
      { jobId, candidateId: candidate.id, patch: { skillMatch: next } },
      {
        onError: (err) =>
          toast.push({ kind: "error", title: "Fehler", message: err.message }),
      },
    );
  };

  return (
    <Card>
      <div className="mb-4 flex items-baseline justify-between gap-3">
        <h3 className="m-0 text-[15px] font-semibold">Skill-Abgleich</h3>
        {canEdit && (
          <span className="text-[11px] text-text-tertiary">
            Klicken, um den Status manuell zu setzen
          </span>
        )}
      </div>
      <div className="flex flex-col gap-4">
        <Group title="Must-Haves der Stelle" color="success-600">
          {jobMustHaves.length === 0 ? (
            <EmptyLine />
          ) : (
            jobMustHaves.map((s) => {
              const met = match.mustHit.includes(s);
              return (
                <ToggleTag
                  key={s}
                  label={s}
                  met={met}
                  icon={met ? Check : X}
                  onClick={() => toggle("mustHaves", s, met)}
                  tagType={met ? "must-met" : "must-missing"}
                  disabled={!canEdit || updateCandidate.isPending}
                />
              );
            })
          )}
        </Group>
        <Group title="Should-Haves" color="warning-600">
          {jobShouldHaves.length === 0 ? (
            <EmptyLine />
          ) : (
            jobShouldHaves.map((s) => {
              const met = match.shouldHit.includes(s);
              return (
                <ToggleTag
                  key={s}
                  label={s}
                  met={met}
                  icon={met ? Zap : X}
                  onClick={() => toggle("shouldHaves", s, met)}
                  tagType={met ? "should-met" : "should-missing"}
                  disabled={!canEdit || updateCandidate.isPending}
                />
              );
            })
          )}
        </Group>
        {otherSkills.length > 0 && (
          <Group title="Weitere Skills" color="text-tertiary">
            {otherSkills.map((s) => (
              <SkillTag key={s} type="other" label={s} />
            ))}
          </Group>
        )}
      </div>
    </Card>
  );
}

function Group({
  title,
  color,
  children,
}: {
  title: string;
  color: string;
  children: ReactElement | ReactElement[];
}): ReactElement {
  return (
    <div>
      <div
        className={`mb-2 text-xs font-semibold uppercase tracking-wider text-${color}`}
      >
        {title}
      </div>
      <div className="flex flex-wrap gap-1.5">{children}</div>
    </div>
  );
}

function EmptyLine(): ReactElement {
  return (
    <span className="text-xs italic text-text-tertiary">
      Keine Skills angegeben.
    </span>
  );
}

interface ToggleTagProps {
  label: string;
  met: boolean;
  icon: typeof Check;
  onClick: () => void;
  tagType: "must-met" | "must-missing" | "should-met" | "should-missing";
  disabled?: boolean;
}

function ToggleTag({
  label,
  met,
  onClick,
  tagType,
  disabled,
}: ToggleTagProps): ReactElement {
  const title = met
    ? "Klicken, um als fehlend zu markieren"
    : "Klicken, um als erfüllt zu markieren";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="inline-flex cursor-pointer items-center rounded-sm p-0 transition hover:opacity-80 disabled:cursor-default disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
      title={title}
      aria-pressed={met ? "true" : "false"}
    >
      <SkillTag type={tagType} label={label} />
    </button>
  );
}
